import React, { FC } from "react";
import styled from "styled-components";
import { Card, Button, Tag, Modal } from "antd";
import { CatalogItem, Location } from "../../queries/auto-generated/schemas";
import {
  getTodaysHours,
  TIME_INPUT_FORMAT,
  OUTPUT_FORMAT,
} from "../../helpers";
import moment from "moment";
import { useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import locationDuck, { ZOOMED_IN } from "../../ducks/location";
import {
  MENU_PAGE_PATH,
  LOCATIONS_PAGE_PATH,
  CHECKOUT_PAGE_PATH,
} from "../../router";
import { RootState } from "../../ducks";
import { InfoCircleOutlined } from "@ant-design/icons";
import Address from "./address";
import Phone from "./phone";
import withMoreInfo from "./withMoreInfo";
import { MapSingleLocation } from "./map-single-location";
import useCart from "../../hooks/useCart";
import { useState } from "react";

const LocationCard: FC<{
  location: Location;
  loading?: boolean;
  mode?: "in-list" | "single" | "preview";
  showActionButton?: boolean;
  showMapPreview?: boolean;
}> = ({
  location,
  loading,
  mode = "in-list",
  showActionButton = true,
  showMapPreview,
}) => {
  let history = useHistory();
  const dispatch = useDispatch();
  const selectedLocationId = useSelector(
    (state: RootState) => state.location.selected
  );

  const { checkCartAvailabilityAtLocation, updateCartAvailability } = useCart();

  const [showConfirm, setShowConfirm] = useState<boolean>(false);
  const [unavailableItems, setUnavailableItems] = useState<
    CatalogItem[] | undefined
  >(undefined);

  const isSelected = selectedLocationId === location.id;

  const editable = mode !== "in-list";
  const isPreview = mode === "preview";

  const todaysHours = getTodaysHours(location);

  const hourStrings = todaysHours
    ? todaysHours.periods.map((period) => {
        const start = moment(period.start_local_time, TIME_INPUT_FORMAT).format(
          OUTPUT_FORMAT
        );
        const end = moment(period.end_local_time, TIME_INPUT_FORMAT).format(
          OUTPUT_FORMAT
        );
        return `${start} - ${end}`;
      })
    : [];

  const times = todaysHours
    ? `${hourStrings.join(" | ")} ${todaysHours.shortTimeZone} (${
        todaysHours.dayOfWeek
      })`
    : "";

  const ButtonWithMoreInfo = withMoreInfo(Button);

  const hoursPreview = (
    <div key="hours_preview">
      <span>{times}</span>
      {times && (
        <ButtonWithMoreInfo
          location={location}
          size="small"
          type="text"
          style={{ marginLeft: "0.5em" }}
        >
          <small>(View hours)</small>
        </ButtonWithMoreInfo>
      )}
    </div>
  );

  const openStatus = todaysHours ? todaysHours.open : null;

  const updateLocation = () => {
    dispatch(locationDuck.actions.selectLocation({ locationId: location.id }));
    const redirectPath = editable ? LOCATIONS_PAGE_PATH : MENU_PAGE_PATH;
    history.push(redirectPath);
    setShowConfirm(false);
    setUnavailableItems(undefined);
  };

  const handleLocationChangeRequest = () => {
    if (!editable) {
      const { hasUnavailableItems, unavailable } =
        checkCartAvailabilityAtLocation(location.id);
      if (hasUnavailableItems) {
        setShowConfirm(true);
        setUnavailableItems(unavailable);
        return;
      } else {
        updateLocation();
      }
    }
  };

  const handleConfirm = () => {
    updateCartAvailability(location.id);
    updateLocation();
  };

  const ActionButton: FC = () => {
    const text = editable ? "Switch location" : "Order here";
    return (
      <>
        <Button
          type={editable ? "text" : "primary"}
          size={editable ? "small" : "large"}
          shape="round"
          onClick={handleLocationChangeRequest}
        >
          {text}
        </Button>
      </>
    );
  };

  const statusTag =
    openStatus !== null &&
    (openStatus ? (
      <StyledTag key="tag_open" color="green">
        Open
      </StyledTag>
    ) : (
      <StyledTag key="tag_closed" color="red">
        Closed
      </StyledTag>
    ));

  const handleClick = () => {
    if (!isSelected && location.coordinates) {
      dispatch(
        locationDuck.actions.updateMapCenter({
          lat: location.coordinates?.latitude,
          lng: location.coordinates?.longitude,
        })
      );
      dispatch(locationDuck.actions.updateMapZoom(ZOOMED_IN));
    }
  };

  const content = [
    showMapPreview && (
      <MapContainer>
        <MapSingleLocation location={location} height="200px" />
      </MapContainer>
    ),
    statusTag,
    location.address && <Address key="address" address={location.address} />,
    location.phone_number && (
      <Phone key="phone" phoneNumber={location.phone_number} />
    ),
    hoursPreview,
  ];

  const PreviewWithMoreInfo = withMoreInfo(Preview);

  return (
    <>
      {isPreview ? (
        <PreviewWithMoreInfo location={location}>
          {`Location: ${location.business_name}`}
          <Button type="text" icon={<InfoCircleOutlined />} />
        </PreviewWithMoreInfo>
      ) : (
        <StyledCard
          title={location.business_name}
          loading={loading}
          extra={showActionButton ? <ActionButton /> : null}
          onClick={handleClick}
          isEditable={editable}
        >
          {content}
        </StyledCard>
      )}
      <Modal
        visible={showConfirm}
        onOk={handleConfirm}
        onCancel={() => {
          setShowConfirm(false);
          setUnavailableItems(undefined);
          history.push(CHECKOUT_PAGE_PATH);
        }}
        okText="Yes, remove from cart"
        cancelText="No, keep in cart"
        okType="danger"
        zIndex={2000}
      >
        <p>
          You have items in your cart that are not available at this location.
          Changing locations will remove these items from your cart.
        </p>
        {unavailableItems && (
          <>
            <p>The following items will be removed:</p>
            <ul>
              {unavailableItems.map((item) => (
                <li key={item.id}>{item.name}</li>
              ))}
            </ul>
          </>
        )}
        <p>Would you like to remove them?</p>
      </Modal>
    </>
  );
};

const StyledCard = styled(Card).withConfig({
  shouldForwardProp: (prop) => !["isEditable"].includes(prop),
})<{ isEditable: boolean }>`
  width: 100%;
  .ant-card-head {
    border-bottom: 0;

    .ant-card-head-title {
      white-space: initial;
      padding-right: 1em;
    }
  }
  .ant-card-body {
    padding-top: 0;
  }
  .ant-card-extra {
    padding-${(p) => (p.isEditable ? "top" : "bottom")}: 0;
  }
`;
const StyledTag = styled(Tag)`
  margin-bottom: 0.5em;
`;

const Preview = styled.p`
  cursor: pointer;
`;

const MapContainer = styled.div`
  margin-bottom: 0.5em;
`;

export default LocationCard;
